/*
 * Cart drawer.
 *
 * The drawer contents are the `cart.bare` template (layout:false, wrapped in
 * #CartPage). Two ways to refresh them:
 *
 *   1. renderFromSections(sections) — the add-to-cart response already carried
 *      the rendered HTML via Shopify's Section Rendering API. No extra request.
 *   2. render() — fetch /cart?view=bare. Fallback, and used for plain refreshes.
 *
 * The drawer opens immediately on add-to-cart and shows a busy state, so the
 * panel never waits on the network before it appears.
 */
class CartDrawer extends HTMLElement {
  // Section ids as keyed in templates/cart.bare.json, in render order.
  static SECTION_IDS = ['cart-header', 'cart-free-shipping', 'cart-items', 'cart-footer'];

  connectedCallback() {
    this.init();
  }

  init() {
    if (this.initialized) return;

    this.drawer = this.querySelector('#CartDrawer');
    this.contents = this.querySelector('[data-cart-drawer-contents]');
    this.cartDrawerCloseButton = this.querySelector('[data-close-cart-drawer]');

    if (!this.drawer || !this.contents) {
      console.error('[cart-drawer] required elements are missing.');
      return;
    }

    this.initialized = true;
    this.preClasses = (this.getAttribute('data-classes-pre') || '').split(' ').filter(Boolean);
    this.postClasses = (this.getAttribute('data-classes-post') || '').split(' ').filter(Boolean);
    this.renderToken = 0;

    if (this.cartDrawerCloseButton) {
      this.cartDrawerCloseButton.addEventListener('click', () => this.close());
    }

    // Delegated so trigger buttons rendered after load still open the drawer.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-cart-drawer]');
      if (trigger) {
        event.preventDefault();
        this.open();
        return;
      }
      if (!this.hasAttribute('data-open')) return;
      if (!event.composedPath().includes(this.drawer)) this.close();
    });

    document.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && this.hasAttribute('data-open')) this.close();
    });

    // Other sections still ask for a refresh with this event.
    document.body.addEventListener('render-cart', () => this.render());

    // An add-to-cart may have supplied the sections before the idle callback.
    // In that case the warm-up request would only duplicate the completed work.
    const warmContents = () => {
      if (this.renderToken === 0) this.render();
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(warmContents, { timeout: 2000 });
    } else {
      setTimeout(warmContents, 1);
    }
  }

  get triggers() {
    return document.querySelectorAll('[data-open-cart-drawer]');
  }

  close() {
    this.removeAttribute('data-open');
    document.body.classList.remove('overflow-hidden');
    this.triggers.forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(item);
    });
    this.postClasses.forEach((item) => this.classList.remove(item));
    this.preClasses.forEach((item) => this.classList.add(item));
  }

  open() {
    this.setAttribute('data-open', '');
    document.body.classList.add('overflow-hidden');
    this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
    this.preClasses.forEach((item) => this.classList.remove(item));
    this.postClasses.forEach((item) => this.classList.add(item));
    if (typeof trapFocus === 'function') trapFocus(this.drawer);
  }

  setBusy(busy) {
    this.contents.toggleAttribute('data-cart-busy', busy);
    this.contents.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /*
   * Commit new drawer markup. `html` is the outer #CartPage element, matching
   * what the previous htmx hx-select="#CartPage" swap produced.
   */
  commit(html, token) {
    if (token !== this.renderToken) return false;
    this.contents.innerHTML = html;
    this.setBusy(false);
    // htmx:afterSwap is what the discount form and other listeners already
    // hook into; keep firing it so nothing downstream has to change.
    document.body.dispatchEvent(new CustomEvent('htmx:afterSwap', { bubbles: true }));
    document.body.dispatchEvent(new CustomEvent('cart:rendered', { bubbles: true }));
    return true;
  }

  /*
   * Fast path: HTML already came back with the add-to-cart response.
   * Returns false when the payload is unusable so the caller can fall back.
   */
  renderFromSections(sections) {
    if (!sections) return false;

    const parts = CartDrawer.SECTION_IDS.map((id) => sections[id]);
    if (parts.some((part) => typeof part !== 'string' || part === '')) return false;

    const token = ++this.renderToken;
    return this.commit('<div id="CartPage">' + parts.join('') + '</div>', token);
  }

  /* Slow path: re-fetch the bare cart template. */
  async render() {
    const token = ++this.renderToken;
    this.setBusy(true);

    try {
      const response = await fetch('/cart?view=bare', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('HTTP ' + response.status);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const page = doc.querySelector('#CartPage');
      if (!page) throw new Error('#CartPage missing from /cart?view=bare');

      this.commit(page.outerHTML, token);
    } catch (error) {
      console.error('[cart-drawer] render failed:', error);
      if (token === this.renderToken) this.setBusy(false);
    }
  }

  /* Section ids to request from the cart AJAX API, Shopify's usual convention. */
  getSectionsToRender() {
    return CartDrawer.SECTION_IDS;
  }
}

customElements.define('cart-drawer', CartDrawer);
